import React from 'react'
import { graphql } from 'gatsby'

/**
 * Renders a WordPress media field (`{ node }` selected with `...CmsImageField`), or nothing
 * when it's empty. `alt` is the fallback when the media item has no alt text.
 *
 * WordPress keeps resized copies of every upload (`srcSet`), so pass `sizes`, the width
 * the image is shown at, and the browser downloads the smallest copy that looks sharp.
 * Width and height reserve the image's space before it loads. Images load when they come
 * near the screen; mark the main image at the top of a page `priority` instead. Background
 * artwork is `decorative`: no alt text, so screen readers skip it.
 */
const CmsImage = ({
  image,
  alt = '',
  sizes = '100vw',
  priority = false,
  decorative = false,
  className,
  ...props
}) => {
  const node = image?.node
  if (!node?.sourceUrl) return null
  const { width, height } = node.mediaDetails || {}

  return (
    <img
      src={node.sourceUrl}
      srcSet={node.srcSet || undefined}
      sizes={node.srcSet ? sizes : undefined}
      width={width || undefined}
      height={height || undefined}
      alt={decorative ? '' : node.altText || alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchpriority={priority ? 'high' : undefined}
      decoding={priority ? undefined : 'async'}
      className={className}
      {...props}
    />
  )
}

// Inline style for using a WordPress media field as a CSS background image.
export const cmsBackground = (image) =>
  image?.node?.sourceUrl ? { backgroundImage: `url(${image.node.sourceUrl})` } : undefined

// Selection for any media field rendered with <CmsImage>: `heroImage { ...CmsImageField }`.
export const query = graphql`
  fragment CmsImageField on WpMediaItemConnectionEdgeType {
    node {
      sourceUrl
      altText
      srcSet
      mediaDetails {
        width
        height
      }
    }
  }
`

export default CmsImage
